import { Box, useTheme } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { useStation } from "../../../../providers/StationProvider";
import Route from "../../../../common/Route";
import { GeoJsonPoint } from "../../../../common/maphelpers";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      height: "100%",
      paddingBottom: theme.spacing(0.5),
    },
    mapRoot: {
      position: "relative",
      height: "100%",
      width: "100%",
      "& .map": {
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: theme.spacing(1)
      }
    },
  })
);

export default function NetworkStationMap(props) {
  const { stations, filteredStations, expanded, setExpanded } = props;
  const { classes } = useStyles();
  const { station } = useStation();
  const containerRef = useRef(null);
  const [map, setMap] = useState(null);
  const theme = useTheme()


  const stationsRef = useRef(stations);
  stationsRef.current = stations;

  // Fetch all stations

  useEffect(() => {
    try {
      const component = containerRef.current;
      if (!component || !station) return;

      component.innerHTML = "";

      const m = new mapboxgl.Map({
        container: component, // container ID
        style: "mapbox://styles/mapbox/streets-v12", // style URL
        center: station?.location?.coordinates, // starting position [lng, lat]
        zoom: 10.5, // starting zoom
        attributionControl: false
      });

      m.loadImage(
        "/assets/icons/map/business.png",
        (error, image) => {
          m?.addImage("station", image, { sdf: true, pixelRatio: 16 });
        }
      );


      const ro = new ResizeObserver(() => {
        if (m || m.forceKilled !== true) {
          setTimeout(() => {
            try {
              m?.resize();
            } catch { }
          }, 0);
        }
      });

      ro.observe(component);

      m.on("load", () => {
        if (m.forceKilled === true) return;
        // Init source layer

        m.addSource("stations_source", {
          type: "geojson",
          data: Route.EmptyGeoJson(),
          cluster: true,
          clusterRadius: 35
        });


        // Layer data
        const p = theme.palette;

        m.addLayer({
          id: "stations_background",
          source: "stations_source",
          type: "circle",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-radius": 20, // Adjust the radius as needed
            "circle-color": ["get", "color"], // Set the desired color
            "circle-stroke-width": 3,
            "circle-stroke-color": "#FFF",
          }
        });

        m.addLayer({
          id: "stations",
          source: "stations_source",
          type: "symbol",
          filter: ["!", ["has", "point_count"]],
          "layout": {
            "icon-image": ["get", "icon"], // Assume you have an icon named "road-works" added to the style
            "icon-size": 1.8,
            "icon-ignore-placement": true,
          },
          "paint": {
            "icon-color": "#FFF"
          }
        });

        m.addLayer({
          id: "stations_text",
          source: "stations_source",
          slot: "elevation-2",
          filter: ["!", ["has", "point_count"]],
          type: "symbol",
          layout: {
            "text-allow-overlap": true,
            "text-ignore-placement": true,
            "text-field": ["get", "displayName"],
            "text-font": [
              "Open Sans Semibold",
              "Arial Unicode MS Bold"
            ],
            "text-size": 16,
            "text-offset": [-0.1, -1.55],
            "text-anchor": "bottom",
            "text-max-width": 7
          },
          paint: {
            "text-halo-color": "#fff",
            "text-halo-width": 2
          }
        });


        // Add cluster layers
        m.addLayer({
          id: "stations_cluster_circles",
          source: "stations_source",
          type: "circle",
          filter: ["has", "point_count"],
          paint: {
            "circle-stroke-width": 3,
            "circle-stroke-color": "#FFF",
            "circle-color": [
              "step",
              ["get", "point_count"],
              p.info.light,
              4,
              p.info.main,
              9,
              p.info.dark,
            ],
            "circle-radius": [
              "step",
              ["get", "point_count"],
              18,
              100,
              30,
              750,
              40
            ]
          }
        });

        m.addLayer({
          id: "stations_cluster_text",
          source: "stations_source",
          type: "symbol",
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["Arial Unicode MS Bold"],
            "text-size": 16,
          },
          paint: {
            "text-color": "#FFF"
          }
        });


        m.on("click", "stations_background", (e) => {
          const props = e.features[0].properties;
          setExpanded(props.stationId);
        });

        if (m.forceKilled !== true) {
          setMap(m);
        }
        // Add features
      });

      return () => {
        m.forceKilled = true;
        setMap(null);
        m?.remove();
        ro?.disconnect();
      };
    } catch (err) {
      console.log(err);
    }
  }, [theme, containerRef, station]);


  useEffect(() => {
    if (!!map && map.forceKilled !== true) {

      if (filteredStations.length > 0) {

        const p = theme.palette;

        let data = filteredStations.filter(t => t.location?.coordinates?.length === 2).map(s => GeoJsonPoint(
          s.location.coordinates,
          {
            stationId: s.id,
            displayName: s.displayName,
            icon: "station",
            color: p.info.main
          }
        ))

        // TODO calc AABB
        const bounds = new mapboxgl.LngLatBounds();
        data.forEach(f => bounds.extend(f.geometry.coordinates));
        
        if (data.length !== 0) {
          map?.fitBounds(bounds, { padding: 64, maxZoom: 13 });
        } else {
          map.flyTo({
            center: station.location.coordinates,
            zoom: 9,
            padding: 64
          });
        }

        map?.getSource("stations_source")?.setData(Route.GeoJson(data));

      } else {

        const bounds = {
          center: station?.location?.coordinates,
          zoom: 9,
          padding: 32
        };

        // set map to station
        map.flyTo(bounds);
        map?.getSource("stations_source")?.setData(Route.EmptyGeoJson());
      }
    }
  }, [map, station, theme, filteredStations]);

  useEffect(() => {
    if (!!expanded && !!map) {
      let expandedStation = stationsRef.current.find(t => t.id === expanded);
      if (expandedStation?.location?.coordinates?.length === 2) {
        // set map to station
        map.flyTo({
          center: expandedStation?.location?.coordinates,
          zoom: 9,
          padding: 32
        });
      }
    }
  }, [expanded, map]);

  return (
    <Box className={classes.root}>
      <div className={classes.mapRoot}>
        <div ref={containerRef} className="map"></div>
      </div>
    </Box>
  );
}

